/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/

import React, { ReactNode, useEffect, useState } from 'react';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { gql, useQuery } from '@apollo/client';
import config from '../../utils/config';

export enum FeatureFlags {
	NewTasksUi = 'participant-experience-tasks-ui',
	NewTasks = 'participant-experience-tasks',
	WebExtensions = 'participant-experience-web-extensions',
	WebExtensionsV2 = 'participant-experience-web-extensions-v2',
	WebExtensionsBeta = 'participant-experience-web-extensions-beta', // v3
}

const developmentFeatureFlags = {
	[FeatureFlags.NewTasksUi]: {
		treatment: 'on',
		config: null,
	},
	[FeatureFlags.NewTasks]: {
		treatment: 'on',
		config: null,
	},
	[FeatureFlags.WebExtensions]: {
		treatment: 'on',
		config:
			'{"chrome":{"installationUrl":"https://chromewebstore.google.com/detail/userzoom-surveys-v3-beta/lfbiacicoefjeollgnldfpopnnnabcep","minVersion":">= 1.0.2","id":"lfbiacicoefjeollgnldfpopnnnabcep"},"firefox":{"installationUrl":"https://pec-cdn4.userzoom.com/addon/ff_pre/userzoom_surveys_v3-1.0.21.xpi","minVersion":">= 1.0.19","id":"extension-dev@userzoom.com_4","installationUrlV2":"https://pec-cdn4.userzoom.com/addon/ff/userzoom_surveys-6.0.1-fx.xpi","minVersionV2":">=6.0.1"},"edge":{"installationUrl":"https://chromewebstore.google.com/detail/userzoom-surveys-v3-beta/lfbiacicoefjeollgnldfpopnnnabcep","minVersion":">= 1.0.2","id":"lfbiacicoefjeollgnldfpopnnnabcep"}}',
	},
	[FeatureFlags.WebExtensionsV2]: {
		treatment: 'on',
		config:
			'{"chrome":{"installationUrl":"https://chrome.google.com/webstore/detail/userzoom-surveys-beta-tes/dpjnooaiedadmbmbdnmngnoglcmmllgm","minVersion":">= 6.2.30"},"firefox":{"installationUrl":"https://pec-cdn4.userzoom.com/addon/ff_pre/userzoom_surveys_devel-5.2.79-fx.xpi","minVersion":">= 5.2.79"},"edge":{"installationUrl":"https://chrome.google.com/webstore/detail/userzoom-surveys-beta-tes/dpjnooaiedadmbmbdnmngnoglcmmllgm","minVersion":">= 6.2.28"}}',
	},
	[FeatureFlags.WebExtensionsBeta]: {
		treatment: 'on',
		config:
			'{"chrome":{"installationUrl":"https://chromewebstore.google.com/detail/userzoom-surveys-v3-beta/lfbiacicoefjeollgnldfpopnnnabcep","minVersion":">= 1.0.2","id":"lfbiacicoefjeollgnldfpopnnnabcep"},"firefox":{"installationUrl":"https://pec-cdn4.userzoom.com/addon/ff_pre/userzoom_surveys_v3-1.0.21.xpi","minVersion":">= 1.0.19","id":"extension-dev@userzoom.com_4","installationUrlV2":"https://pec-cdn4.userzoom.com/addon/ff/userzoom_surveys-6.0.1-fx.xpi","minVersionV2":">=6.0.1"},"edge":{"installationUrl":"https://chromewebstore.google.com/detail/userzoom-surveys-v3-beta/lfbiacicoefjeollgnldfpopnnnabcep","minVersion":">= 1.0.2","id":"lfbiacicoefjeollgnldfpopnnnabcep"}}',
	},
};

const splitConfig = {
	core: {
		authorizationKey: config().splitIOApiKey,
		key: 'default',
	},
	features: developmentFeatureFlags,
};

const accountQuery = gql`
	query {
		participant {
			account
		}
	}
`;

const SplitIo: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [splitIoConfig, setSplitIoConfig] = useState(splitConfig);
	const { error, data } = useQuery(accountQuery);

	useEffect(() => {
		if (data) {
			setSplitIoConfig((prevConfig) => ({
				...prevConfig,
				core: { ...prevConfig.core, key: data.participant.account },
			}));
		}
	}, [error, data]);

	return data ? (
		<SplitFactoryProvider config={splitIoConfig} updateOnSdkTimedout>
			{({ isReady, hasTimedout }) => (isReady || hasTimedout ? children : <div id="split-not-ready" />)}
		</SplitFactoryProvider>
	) : null;
};

export default SplitIo;
